const _excluded = ["children", "size", "variant", "color", "disabled", "selected", "appearance"],
  _excluded2 = ["label", "size", "variant", "color", "disabled", "selected", "startIcon", "onKeyDown", "onRemove"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import Cross from "@bolteu/kalep-react-icons/dist/Cross";
import { cx } from "../../helpers/utils/cx";
import { selectChipColors } from "./Chip.utils";
const sizes = {
  xs: "h-6 px-2",
  sm: "h-7 px-3",
  md: "h-9 px-4"
};

/**
 * Always set the size prop to the same value as the Chip size.
 * @returns IconElement with given size prop.
 */
function withSize(icon, size) {
  const actualSize = size === "md" ? "md" : "xs";
  return /*#__PURE__*/React.cloneElement(icon, {
    size: actualSize
  });
}
const BaseChip = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      children,
      size,
      variant,
      color,
      disabled,
      selected,
      appearance: appearanceProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  // TODO: Remove this fallback to color prop and color prop itself in a major version
  const appearance = appearanceProp ?? color ?? "neutral";
  const isClickable = typeof props.onClick === "function" && !disabled;
  const isOutlined = variant === "outline";
  const isSimple = variant === "simple";
  const isSelected = selected || false;
  const isInvertedColorScheme = isSimple || isSelected;
  let customPropertiesOverride;

  // Configures Chip coloring with CSS Custom Properties
  if (appearance) {
    customPropertiesOverride = selectChipColors(isInvertedColorScheme, disabled, appearance);
  }
  return /*#__PURE__*/React.createElement("span", _extends({}, props, {
    ref: ref,
    className: cx(["chip", isSelected && "selected", isSimple && "simple"],
    // check Chip.css
    "bolt-font-body-s-regular truncate", isSimple ? "cursor-default" : "cursor-pointer", isClickable && "active:scale-975 active:ease-in-out active:duration-100", isOutlined && !selected && "bg-special-nulled dark:bg-special-nulled", disabled && "pointer-events-none", sizes[size]),
    style: customPropertiesOverride
  }), children);
});
function RemoveChipButton(_ref2) {
  let {
    onRemove,
    size,
    disabled
  } = _ref2;
  return /*#__PURE__*/React.createElement("span", {
    onClick: onRemove,
    "aria-hidden": "true",
    "data-testid": "chip-remove",
    className: cx("h-full flex items-center pe-1 ps-2 -mx-2 cursor-pointer", {
      "pointer-events-none cursor-not-allowed": disabled
    })
  }, withSize( /*#__PURE__*/React.createElement(Cross, null), size));
}
export const Chip = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
      label,
      size = "md",
      variant = "filled",
      color = "neutral",
      disabled = false,
      selected = false,
      startIcon,
      onKeyDown,
      onRemove
    } = _ref3,
    passThroughProps = _objectWithoutProperties(_ref3, _excluded2);
  const isRemovable = typeof onRemove === "function";
  const baseProps = {
    size,
    variant,
    color,
    disabled,
    selected
  };
  const variantProps = {};
  if (isRemovable) {
    variantProps.role = "button";
    variantProps.tabIndex = "0";
    variantProps["aria-disabled"] = disabled;
  }
  const content = [/*#__PURE__*/React.createElement("span", {
    className: cx("truncate"),
    key: "chip-content"
  }, label)];
  if (startIcon) {
    content.unshift( /*#__PURE__*/React.createElement("span", {
      className: cx("-ms-1 [&>svg]:block"),
      key: "chip-icon"
    }, withSize(startIcon, size)));
  }
  if (isRemovable) {
    content.push( /*#__PURE__*/React.createElement(RemoveChipButton, {
      onRemove: onRemove,
      size: size,
      disabled: disabled,
      key: "chip-remove"
    }));
  }
  const handleKeyDown = React.useCallback(event => {
    if (typeof onKeyDown === "function") {
      onKeyDown(event);
    }
    if (isRemovable && (event.key === "Backspace" || event.key === "Delete")) {
      onRemove(event);
    }
  }, [isRemovable, onRemove, onKeyDown]);
  return /*#__PURE__*/React.createElement(BaseChip, _extends({
    ref: ref,
    onKeyDown: handleKeyDown
  }, baseProps, variantProps, passThroughProps), content);
});